import { ChevronDown } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetGroups2 } from "../api/api.get";
import { useGroupContext } from "../providers/groups.provider";

const colors = [
  "from-blue-400 to-blue-600",
  "from-purple-400 to-purple-600",
  "from-green-400 to-green-600",
  "from-pink-400 to-pink-600",
  "from-orange-400 to-amber-600",
];

export default function GroupSelectComponent() {
  const { defaultGroupId, changeDefaultGroupId } = useGroupContext();
  const { data: groups } = useGetGroups2();

  const [isGroupMenuOpen, setIsGroupMenuOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isGroupMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsGroupMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isGroupMenuOpen]);

  const currGroupRef = useMemo(() => {
    if (groups) {
      return groups.find((group) => group.id === defaultGroupId);
    }
    return undefined;
  }, [groups, defaultGroupId]);

  const handleChange = (id: string) => {
    changeDefaultGroupId(id);
    setIsGroupMenuOpen(false);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={() => setIsGroupMenuOpen(!isGroupMenuOpen)}
        className="flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-300/10 to-blue-600/10 border border-primary/30 rounded-full hover:border-primary/50"
      >
        <img
          src="/assets/icons/groups.svg"
          width={18}
          height={18}
          alt="group-icon"
        />
        <span className="text-sm font-semibold text-foreground">
          {currGroupRef?.name}
        </span>
        <ChevronDown className="w-4 h-4 text-muted-foreground" />
      </button>

      {/* Group Dropdown Menu */}
      {isGroupMenuOpen && (
        <div className="absolute right-0 mt-2 w-56 bg-gradient-to-r from-purple-50 to-blue-100 rounded-xl shadow-lg border border-slate-200 z-[999] py-2">
          <p className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Switch Group
          </p>
          {groups?.map((group, idx) => (
            <button
              key={group.id}
              onClick={() => handleChange(group.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition text-left ${
                currGroupRef?.id === group.id
                  ? "bg-slate-50 border-l-4 border-primary"
                  : ""
              }`}
            >
              <div
                className={`w-3 h-3 rounded-full bg-gradient-to-r ${colors[idx]}`}
              ></div>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">
                  {group.name}
                </p>
              </div>
              {defaultGroupId === group.id && (
                <span className="text-primary font-bold">✓</span>
              )}
            </button>
          ))}

          {/* Divider */}
          <div className="border-t border-slate-200 my-2"></div>

          {/* Create New Group */}
          <button
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition text-left"
            onClick={() => {
              setIsGroupMenuOpen(false);
              navigate(`/groups`);
            }}
          >
            <div className="w-3 h-3 rounded-full border-2 border-dashed border-slate-300"></div>
            <p className="text-sm font-medium text-primary">Manage Groups</p>
          </button>
        </div>
      )}
    </div>
  );
}
